import React, { useState, useEffect, useCallback } from 'react';
import { Star, Search, RefreshCw, Filter, TrendingUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { toast } from '@/components/ui/use-toast';
import { adminAPI } from '@/services/api';
import DataTable from '@/components/ui/data-table';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';

const RatingManagement = ({ clientId, onStatisticsUpdate }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [ratings, setRatings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statistics, setStatistics] = useState({
    total: 0,
    average: 0,
    distribution: { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 },
  });

  const loadInitialData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getClientRatings(clientId);
      const ratingsData = response.data.results || response.data || [];
      setRatings(ratingsData);
      
      const total = ratingsData.length;
      const sum = ratingsData.reduce((acc, r) => acc + (Number(r.rating) || 0), 0);
      const average = total > 0 ? (sum / total).toFixed(1) : 0;
      
      const distribution = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
      ratingsData.forEach(r => {
        const rating = Number(r.rating);
        if (rating >= 1 && rating <= 5) {
          distribution[rating]++;
        }
      });
      
      const stats = {
        total,
        average: parseFloat(average),
        distribution,
      };
      setStatistics(stats);
      
      if (onStatisticsUpdate) {
        onStatisticsUpdate(stats);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des évaluations:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de charger les évaluations',
        variant: 'destructive',
      });
      setRatings([]);
    } finally {
      setLoading(false);
    }
  }, [clientId, onStatisticsUpdate]);

  useEffect(() => {
    if (clientId) {
      loadInitialData();
    }
  }, [clientId, loadInitialData]);

  const handleRefresh = async () => {
    await loadInitialData();
    toast({
      title: 'Succès',
      description: 'Données mises à jour',
    });
  };

  const filteredRatings = ratings.filter(rating => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      rating.transporter?.organisation_name?.toLowerCase().includes(searchLower) ||
      rating.comment?.toLowerCase().includes(searchLower) ||
      rating.rating?.toString().includes(searchLower)
    );
  });

  const renderStars = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;
    
    for (let i = 0; i < 5; i++) {
      if (i < fullStars) {
        stars.push(<Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />);
      } else if (i === fullStars && hasHalfStar) {
        stars.push(<Star key={i} className="w-4 h-4 fill-yellow-400/50 text-yellow-400" />);
      } else {
        stars.push(<Star key={i} className="w-4 h-4 fill-none text-gray-300" />);
      }
    }
    return stars;
  };

  const columns = [
    {
      id: 'transporter',
      header: 'Transporteur',
      accessorKey: 'transporter',
      cell: ({ row }) => {
        const rating = row.original;
        const transporter = rating.transporter;
        return (
          <div className="flex items-center gap-3">
            <Avatar className="h-8 w-8">
              <AvatarImage 
                src={`https://api.dicebear.com/7.x/initials/svg?seed=${transporter?.organisation_name || 'Transporteur'}`} 
                alt={transporter?.organisation_name} 
              />
              <AvatarFallback className="text-xs">
                {(transporter?.organisation_name || 'T')[0]}
              </AvatarFallback>
            </Avatar>
            <div className="text-sm">
              <p className="font-medium">{transporter?.organisation_name || 'N/A'}</p>
            </div>
          </div>
        );
      },
    },
    {
      id: 'booking',
      header: 'Réservation',
      accessorKey: 'booking',
      cell: ({ row }) => {
        const rating = row.original;
        return (
          <div className="text-sm">
            {rating.booking?.id ? (
              <span className="font-mono text-muted-foreground">{rating.booking.id.substring(0, 8)}...</span>
            ) : (
              <span className="text-muted-foreground">N/A</span>
            )}
          </div>
        );
      },
    },
    {
      id: 'rating',
      header: 'Note',
      accessorKey: 'rating',
      cell: ({ row }) => {
        const rating = row.original;
        const ratingValue = Number(rating.rating) || 0;
        return (
          <div className="flex items-center gap-2">
            <div className="flex items-center">
              {renderStars(ratingValue)}
            </div>
            <span className="text-sm font-medium">{ratingValue.toFixed(1)}/5</span>
          </div>
        );
      },
    },
    {
      id: 'comment',
      header: 'Commentaire',
      accessorKey: 'comment',
      cell: ({ row }) => {
        const rating = row.original;
        return (
          <div className="text-sm text-muted-foreground max-w-md truncate">
            {rating.comment || 'Aucun commentaire'}
          </div>
        );
      },
    },
    {
      id: 'created_at',
      header: 'Date',
      accessorKey: 'created_at',
      cell: ({ row }) => {
        const rating = row.original;
        return (
          <span className="text-sm text-muted-foreground">
            {rating.created_at ? new Date(rating.created_at).toLocaleDateString('fr-FR') : 'N/A'}
          </span>
        );
      },
    },
  ];

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <CardTitle className="flex items-center gap-2">
            <Star className="w-5 h-5 text-teal-500" />
            Évaluations
          </CardTitle>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="icon"
              onClick={handleRefresh}
              disabled={loading}
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
            <Button
              variant="outline"
              size="icon"
              onClick={() => setShowFilters(!showFilters)}
            >
              <Filter className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {/* Statistiques */}
        <div className="grid grid-cols-2 sm:grid-cols-7 gap-4">
          <div className="bg-muted/50 rounded-lg p-3">
            <p className="text-2xl font-bold text-foreground">{statistics.total}</p>
            <p className="text-xs text-muted-foreground">Total</p>
          </div>
          <div className="bg-yellow-500/10 rounded-lg p-3">
            <p className="text-2xl font-bold text-yellow-500">{statistics.average}</p>
            <p className="text-xs text-muted-foreground">Moyenne</p>
          </div>
          <div className="bg-green-500/10 rounded-lg p-3">
            <p className="text-2xl font-bold text-green-500">{statistics.distribution[5]}</p>
            <p className="text-xs text-muted-foreground">5 étoiles</p>
          </div>
          <div className="bg-blue-500/10 rounded-lg p-3">
            <p className="text-2xl font-bold text-blue-500">{statistics.distribution[4]}</p>
            <p className="text-xs text-muted-foreground">4 étoiles</p>
          </div>
          <div className="bg-orange-500/10 rounded-lg p-3">
            <p className="text-2xl font-bold text-orange-500">{statistics.distribution[3]}</p>
            <p className="text-xs text-muted-foreground">3 étoiles</p>
          </div>
          <div className="bg-red-500/10 rounded-lg p-3">
            <p className="text-2xl font-bold text-red-500">{statistics.distribution[2]}</p>
            <p className="text-xs text-muted-foreground">2 étoiles</p>
          </div>
          <div className="bg-gray-500/10 rounded-lg p-3">
            <p className="text-2xl font-bold text-gray-500">{statistics.distribution[1]}</p>
            <p className="text-xs text-muted-foreground">1 étoile</p>
          </div>
        </div>

        <Separator />

        {/* Filtres */}
        {showFilters && (
          <div className="bg-muted/30 rounded-lg p-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground w-4 h-4" />
              <Input
                placeholder="Rechercher par transporteur, commentaire..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
        )}

        {/* Tableau */}
        <div className="min-h-[400px]">
          <DataTable
            data={filteredRatings}
            columns={columns}
            loading={loading}
            searchable={false}
            pageSize={10}
          />
        </div>
      </CardContent>
    </Card>
  );
};

export default RatingManagement;

